import { useFormikContext } from 'formik';
import * as _ from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  LayoutRectangle,
  Modal,
  Platform,
  TextInput,
  TextInputProps,
  TextProps,
  TouchableHighlightProps,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native';
import { FlatList, TouchableHighlight } from 'react-native-gesture-handler';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';
import Colors from '../../constants/Colors';
import ListItem from '../list/ListItem';
import ListItemText from '../list/ListItemText';
import { useFormContext } from './Form';
import { FormInputVariant } from './FormInput';
import Spacer from '../Spacer';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import elementIDs from '../../../elementIDs';

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledTouchableHighlight = styled.TouchableHighlight<{
  hasHelperText: boolean;
  variant?: FormInputVariant;
}>`
  position: relative;
  background-color: ${p =>
    p.variant === 'survey'
      ? Colors.primary
      : p.variant === 'inline'
        ? 'rgba(255, 255, 255, 0)'
        : '#fff'};
  border: ${p =>
    p.variant !== 'inline' ? `1px solid ${Colors.formInputBorder}` : 'none'};
  border-radius: ${p => (p.variant === 'survey' ? 26.5 : 8)}px;
  padding: ${p => (p.variant === 'inline' ? '3' : '16')}px;
  margin-bottom: ${p =>
    p.hasHelperText ? 4 : p.variant === 'inline' ? 0 : 12}px;
  /* color: #B3B3B3 */
  height: 64px;
  justify-content: center;
` as React.FC<
  TouchableHighlightProps & {
    hasHelperText: boolean;
    ref: any;
    variant?: FormInputVariant;
  }
>;

const StyledText = styled.Text<{
  value?: string;
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  color: ${p =>
    p.value
      ? p.variant === 'survey'
        ? '#fff'
        : p.variant === 'inline'
          ? `${Colors.primaryPurple}`
          : '#000'
      : '#bbbb'};
  font-size: ${p => (p.variant === 'inline' ? '16' : '18')}px;
  font-weight: ${p => (p.variant === 'inline' ? 'bold' : 'normal')};
  padding-right: 32px;

  padding-left: ${p => (p.variant === 'survey' ? 5 : 0)}px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  /* white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis; */
` as React.FC<
  TextProps & {
    value?: string;
    variant?: FormInputVariant;
    empty?: boolean;
  }
>;

const StyledDropDownIcon = styled.View<{ variant?: FormInputVariant }>`
  position: absolute;
  top: 0px;
  right: 0px;
`;

const StyledTextInput = styled.TextInput<{
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  background-color: ${p => (p.variant === 'survey' ? Colors.primary : '#fff')};
  color: ${p =>
    p.variant === 'survey'
      ? Colors.lightText
      : p.variant === 'inline'
        ? Colors.primaryPurple
        : Colors.darkText};
  border: ${p =>
    p.variant === 'inline' ? 'none' : `1px solid ${Colors.formInputBorder}`};
  border-radius: ${p =>
    p.variant === 'survey' ? 26.5 : p.variant === 'inline' ? 0 : 8}px;
  padding: ${p =>
    p.variant === 'inline'
      ? '3px'
      : p.variant === 'survey'
        ? Platform.OS === 'android'
          ? '16px 16px 16px 21px'
          : '16px 16px 16px 21px'
        : '16px'};
  font-size: ${p => (p.variant === 'inline' ? '16' : '18')}px;
  max-height: ${p =>
    Platform.OS === 'web'
      ? 64
      : Platform.OS === 'android'
        ? p.variant === 'inline'
          ? 24
          : 64
        : 64}px;
  height: 64px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  /* color: #B3B3B3 */
  font-weight: ${p => (p.variant === 'inline' ? 'bold' : 'normal')};
` as React.FC<
  TextInputProps & {
    ref: any;
    variant?: FormInputVariant;
    empty?: boolean;
  }
>;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 14px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const StyledModalContainer = styled.TouchableOpacity`
  width: 100%;
  height: 100%;
`;

const StyledPosition = styled.View<{ rect?: LayoutRectangle }>`
  position: absolute;
  top: ${p => p.rect?.y || 0}px;
  left: ${p => p.rect?.x || 0}px;
  width: ${p => p.rect?.width || 0}px;
` as React.FC<{ rect?: LayoutRectangle }>;

const FormPickerMenu = styled.View<{ variant?: FormInputVariant }>`
  /* border: 1px solid #000; */
  background-color: #fff;
  border-radius: ${p => (p.variant === 'survey' ? 8 : 20)}px;
  margin-top: ${p => (p.variant === 'survey' ? 12 : 0)}px;
  box-shadow: 0 8px 8px rgba(50, 50, 71, 0.08);
  elevation: 5;
  z-index: 1;
  max-height: 200px;
` as React.FC<{ variant?: FormInputVariant }>;
export interface FormSelectOption {
  value: string | number;
  label?: string;
}
export interface FormSelectProps {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  variant?: FormInputVariant;
  autocomplete?: boolean;
  options: string[] | FormSelectOption[];
  testID?: string;
}

const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  testID,
  helperText,
  placeholder,
  autocomplete,
  variant = 'outlined',
  ...props
}) => {
  // const { scrollDeltaY } = useAppContext();
  // const scrollDeltaY = 0;
  const insets = useSafeAreaInsets();
  const touchableHighlightRef = useRef<TouchableHighlight>(null!);
  const textInputRef = useRef<TextInput>(null!);
  const [openMenu, setOpenMenu] = useState(false);
  const [layoutRect, setLayoutRect] = useState<LayoutRectangle>();
  const { handleChange, handleBlur, touched, errors, values, setFieldValue } =
    useFormikContext<any>();
  const { setFocusedInput, addInput, focusNext } = useFormContext();
  const options = props.options.map((option: string | FormSelectOption) =>
    typeof option === 'string' || typeof option === 'number'
      ? { label: option, value: option }
      : option
  );
  const error = _.get(touched, name) && _.get(errors, name);
  const value = _.get(values, name) || '';

  // useEffect(() => {
  //   setLayoutRect(layoutRect =>
  //     layoutRect
  //       ? {
  //           ...layoutRect,
  //           y: layoutRect.y - scrollDeltaY
  //         }
  //       : undefined
  //   );
  // }, [scrollDeltaY]);

  useLayoutEffect(() => {
    setOpenMenu(false);
  }, [useWindowDimensions().width]);

  return (
    <View>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}

      <StyledTouchableHighlight
        testID={elementIDs.formSelectID}
        variant={variant}
        ref={(touchableHighlight: TouchableHighlight) => {
          touchableHighlightRef.current = touchableHighlight;
        }}
        underlayColor={Colors.primary + '11'} // add alpha hex
        onPress={() => {
          (touchableHighlightRef.current as any).measureInWindow(
            (x: number, y: number, width: number, height: number) => {
              // y -= scrollDeltaY; // TODO: need to get scroll offset if in a scroll view
              setLayoutRect({
                x,
                y: y + (Platform.OS === 'android' ? insets.top : 0),
                width,
                height
              });
              setOpenMenu(true);
            }
          );
        }}
        hasHelperText={Boolean(helperText)}
      >
        <View>
          <StyledText
            value={value}
            numberOfLines={1}
            variant={variant}
            empty={!values[name]}
          >
            {options.find(o => o.value === value)?.label || placeholder || ' '}
          </StyledText>
          <StyledDropDownIcon variant={variant} testID={testID}>
            {variant === 'survey' ? (
              <Feather name='chevron-down' size={40} color='#fff' />
            ) : variant === 'inline' ? (
              <Feather
                name='chevron-down'
                size={18}
                color={Colors.primaryPurple}
              />
            ) : (
              <Feather name='chevron-down' size={24} color={Colors.primary} />
            )}
          </StyledDropDownIcon>
        </View>
      </StyledTouchableHighlight>

      {helperText && <HelperText>{helperText}</HelperText>}
      <Modal
        transparent
        onRequestClose={() => setOpenMenu(false)}
        visible={openMenu}
      >
        <StyledModalContainer
          onPress={() => setOpenMenu(false)}
          activeOpacity={0}
        >
          <StyledPosition rect={layoutRect}>
            <TouchableOpacity onPress={() => setOpenMenu(false)}>
              <StyledTextInput
                empty={!values[name]}
                variant={variant}
                editable={autocomplete || false}
                blurOnSubmit={false}
                ref={(textInput: TextInput) => {
                  textInputRef.current = textInput;
                  addInput(textInput);
                }}
                placeholderTextColor='#bbb'
                placeholder={placeholder}
                {...props}
                value={String(value) || ''}
                onChangeText={handleChange(name)}
                onFocus={() => {
                  setFocusedInput(textInputRef.current);
                }}
                onBlur={event => {
                  handleBlur(name)(event);
                  setFocusedInput(undefined);
                }}
                onSubmitEditing={() => focusNext()}
                pointerEvents='none'
              />
            </TouchableOpacity>
            <Spacer size={0.5} />
            <FormPickerMenu variant={variant}>
              <FlatList
                data={options}
                {...props}
                keyExtractor={item => item.value.toString()}
                renderItem={({ item }) => (
                  <ListItem
                    style={{
                      height: 64
                    }}
                    onPress={() => {
                      setOpenMenu(false);
                      setFieldValue(name, item.value);
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      {...props}
                      primaryTextColor='#615E6D'
                    />
                  </ListItem>
                )}
              />
            </FormPickerMenu>
          </StyledPosition>
        </StyledModalContainer>
      </Modal>
    </View>
  );
};

export default FormSelect;
